import React, { useEffect, useState, useCallback } from 'react';
import { todayIso, fmtDateDayMonth, fmtDateTimeShort } from '../utils/dateUtils';

const OUTCOME_STYLE = {
  SUCCESS:    { bg: '#dcfce7', color: '#166534' },
  FAILED:     { bg: '#fee2e2', color: '#991b1b' },
  HTTP_ERROR: { bg: '#fef3c7', color: '#92400e' },
  ERROR:      { bg: '#fee2e2', color: '#991b1b' },
};

const TRIGGER_REASONS = [
  'RESERVATION_CREATED', 'RESERVATION_MODIFIED', 'STATUS_CANCELLED',
  'CHECKED_IN', 'WALKIN_CHECKIN', 'CHECKED_OUT', 'ROOM_CHANGED',
  'ROOM_BLOCKED', 'ROOM_UNBLOCKED', 'MANUAL_CONTROL', 'BULK_CONTROL', 'FULL_REBUILD'
];

function OutcomeBadge({ outcome }) {
  const s = OUTCOME_STYLE[outcome] || { bg: '#f3f4f6', color: 'var(--theme-text)' };
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: 700 }}>
      {outcome || '—'}
    </span>
  );
}

const truncate = (str, len) => {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '...' : str;
};

const formatTime = (ts) => {
  if (!ts) return '—';
  return fmtDateTimeShort(ts);
};

const formatDateShort = (d) => {
  if (!d) return '—';
  return fmtDateDayMonth(d);
};

function PmsCmPushLog({ token, selectedProperty }) {
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [triggerFilter, setTriggerFilter] = useState('');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    try {
      let url = `/api/v1/channel-manager/push-log?property_id=${selectedProperty.id}&from_date=${fromDate}&to_date=${toDate}&limit=200`;
      if (triggerFilter) url += `&trigger_reason=${encodeURIComponent(triggerFilter)}`;
      if (outcomeFilter) url += `&outcome=${encodeURIComponent(outcomeFilter)}`;
      const res = await fetch(url, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load');
      setRows(data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load push log');
    } finally {
      setLoading(false);
    }
  }, [selectedProperty, fromDate, toDate, triggerFilter, outcomeFilter, headers]);

  useEffect(() => { load(); }, [load]);

  const summary = {
    total: rows.length,
    success: rows.filter((r) => r.outcome === 'SUCCESS').length,
    failed: rows.filter((r) => r.outcome === 'FAILED').length,
    httpErrors: rows.filter((r) => r.outcome === 'HTTP_ERROR').length,
    errors: rows.filter((r) => r.outcome === 'ERROR').length,
  };

  return (
    <div className="page-shell">
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.5rem' }}>
        Front Office <span style={{ color: '#cbd5e0' }}>&rsaquo;</span> <span style={{ color: 'var(--theme-primary)', fontWeight: 600 }}>CM Inventory Push Log</span>
      </div>
      <h3 style={{ margin: '0 0 0.75rem', color: 'var(--theme-primary)' }}>Channel Manager Inventory Push Log</h3>

      {!selectedProperty?.id && (
        <div className="error-banner">Please select a property first.</div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--theme-text)' }}>From:</label>
        <input type="date" value={fromDate} onChange={(e) => { const v = e.target.value; setFromDate(v); if (toDate && v && toDate < v) setToDate(v); }}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
        <label style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--theme-text)' }}>To:</label>
        <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => { const v = e.target.value; setToDate(v); if (fromDate && v && fromDate > v) setFromDate(v); }}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
        <select value={triggerFilter} onChange={(e) => setTriggerFilter(e.target.value)}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }}>
          <option value="">All Triggers</option>
          {TRIGGER_REASONS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={outcomeFilter} onChange={(e) => setOutcomeFilter(e.target.value)}
          style={{ padding: '0.35rem 0.6rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.82rem' }}>
          <option value="">All Outcomes</option>
          {['SUCCESS', 'FAILED', 'HTTP_ERROR', 'ERROR'].map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <button onClick={load} disabled={loading}
          style={{ padding: '0.4rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        <button onClick={() => { const t = todayIso(); setFromDate(t); setToDate(t); }}
          style={{ padding: '0.4rem 0.9rem', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', color: 'var(--theme-text)' }}>
          Today
        </button>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: '0.8rem' }}>{error}</div>}

      {/* Summary cards */}
      {rows.length > 0 && (
        <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          {[
            { label: 'Total Pushes', value: summary.total, bg: '#eff6ff', color: '#1e40af' },
            { label: 'Success', value: summary.success, bg: '#dcfce7', color: '#166534' },
            { label: 'Failed', value: summary.failed, bg: '#fee2e2', color: '#991b1b' },
            { label: 'HTTP Errors', value: summary.httpErrors, bg: '#fef3c7', color: '#92400e' },
            { label: 'Errors', value: summary.errors, bg: '#fee2e2', color: '#991b1b' },
          ].map((c) => (
            <div key={c.label} style={{ background: c.bg, color: c.color, borderRadius: '8px', padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 600, minWidth: '90px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>{c.value}</div>
              <div style={{ fontSize: '0.72rem', marginTop: '1px' }}>{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ color: '#6b7280', padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{ color: '#6b7280', padding: '2rem', textAlign: 'center', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          No inventory pushes found for the selected period.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: 'var(--theme-border-light)' }}>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)', whiteSpace: 'nowrap' }}>Date &amp; Time</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)', whiteSpace: 'nowrap' }}>Room Type</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Trigger</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)', whiteSpace: 'nowrap' }}>Date Range</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Outcome</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'center', borderBottom: '2px solid var(--theme-border)' }}>HTTP</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>Error</th>
                <th style={{ padding: '0.5rem 0.6rem', textAlign: 'left', borderBottom: '2px solid var(--theme-border)' }}>InfoDays</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.log_id}>
                  <tr
                    onClick={() => setExpandedRow((prev) => prev === r.log_id ? null : r.log_id)}
                    style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer', background: expandedRow === r.log_id ? 'var(--theme-bg-light)' : undefined }}
                  >
                    <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap', color: 'var(--theme-text)' }}>{formatTime(r.pushed_at)}</td>
                    <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap', fontWeight: 600, color: '#1e40af', fontSize: '0.8rem' }}>
                      {r.room_type_name || (r.room_type_id ? `#${r.room_type_id}` : '—')}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem' }}>
                      <span style={{ background: '#e0e7ff', color: '#3730a3', borderRadius: '4px', padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {r.trigger_reason || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', whiteSpace: 'nowrap', color: 'var(--theme-text)', fontSize: '0.8rem' }}>
                      {formatDateShort(r.start_date)} — {formatDateShort(r.end_date)}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem' }}><OutcomeBadge outcome={r.outcome} /></td>
                    <td style={{ padding: '0.45rem 0.6rem', textAlign: 'center', fontFamily: 'monospace', fontSize: '0.78rem',
                      color: r.http_status === 200 ? 'var(--theme-success)' : (r.http_status >= 400 ? 'var(--theme-danger)' : '#6b7280') }}>
                      {r.http_status || '—'}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', color: r.error_message ? 'var(--theme-danger)' : '#6b7280', fontSize: '0.78rem', maxWidth: '200px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {truncate(r.error_message, 80)}
                    </td>
                    <td style={{ padding: '0.45rem 0.6rem', fontFamily: 'monospace', fontSize: '0.72rem', color: '#6b7280', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {truncate(r.info_days, 60)}
                    </td>
                  </tr>
                  {/* Expanded detail row */}
                  {expandedRow === r.log_id && (
                    <tr>
                      <td colSpan={8} style={{ padding: '0.7rem 1rem', background: 'var(--theme-bg-light)', borderBottom: '2px solid var(--theme-border)' }}>
                        {r.request_url && (
                          <div style={{ marginBottom: '0.5rem', fontSize: '0.72rem', color: '#6b7280' }}>
                            <b>POST</b> {r.request_url}
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.75rem', color: 'var(--theme-text)', marginBottom: '0.3rem' }}>Request Body (sent to EaseRoom)</div>
                            <pre style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem', fontSize: '0.72rem',
                              maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, color: 'var(--theme-text)' }}>
                              {r.request_body || '(not captured for this push)'}
                            </pre>
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.75rem', color: 'var(--theme-text)', marginBottom: '0.3rem' }}>Response Body</div>
                            <pre style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem', fontSize: '0.72rem',
                              maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, color: 'var(--theme-text)' }}>
                              {r.response_body || '(empty)'}
                            </pre>
                          </div>
                        </div>
                        <div style={{ marginTop: '0.6rem' }}>
                          <div style={{ fontWeight: 700, fontSize: '0.75rem', color: 'var(--theme-text)', marginBottom: '0.3rem' }}>InfoDays Payload</div>
                          <pre style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem', fontSize: '0.72rem',
                            maxHeight: '120px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0, color: 'var(--theme-text)' }}>
                            {r.info_days || '(empty)'}
                          </pre>
                        </div>
                        {r.easeroom_room_type_id && (
                          <div style={{ marginTop: '0.4rem', fontSize: '0.72rem', color: '#6b7280' }}>
                            EaseRoom Room Type ID: <b>{r.easeroom_room_type_id}</b>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PmsCmPushLog;
